import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X, Send } from "lucide-react";
import { Button } from "./ui/Button";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId?: string;
  onSubmit?: () => void;
}

export function FeedbackModal({ isOpen, onClose, documentId, onSubmit }: FeedbackModalProps) {
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      alert("Please select a rating");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          document_id: documentId,
          rating,
          feedback_text: feedbackText.trim() || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit feedback");
      }

      setIsSubmitted(true);
      onSubmit?.();

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
        // Reset state after modal closes
        setTimeout(() => {
          setRating(0);
          setFeedbackText("");
          setIsSubmitted(false);
        }, 300);
      }, 2000);
    } catch (error) {
      console.error("Feedback submission error:", error);
      alert(error instanceof Error ? error.message : "Failed to submit feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
    // Reset state after modal closes
    setTimeout(() => {
      setRating(0);
      setFeedbackText("");
      setIsSubmitted(false);
    }, 300);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleSkip}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 pointer-events-auto overflow-hidden"
            >
              {isSubmitted ? (
                // Success State
                <div className="p-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.5 }}
                    className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <svg
                      className="w-8 h-8 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </motion.div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    Thank you!
                  </h3>
                  <p className="text-slate-600">
                    Your feedback helps us improve Resume Aligned.
                  </p>
                </div>
              ) : (
                // Feedback Form
                <>
                  {/* Header */}
                  <div className="p-6 pb-4 border-b border-slate-100">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-slate-900">
                          How was your experience?
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">
                          Your feedback helps us improve
                        </p>
                      </div>
                      <button
                        onClick={handleSkip}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-6">
                    {/* Star Rating */}
                    <div className="flex flex-col items-center">
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setRating(star)}
                            onMouseEnter={() => setHoveredRating(star)}
                            onMouseLeave={() => setHoveredRating(0)}
                            className="transition-transform hover:scale-110 focus:outline-none"
                          >
                            <Star
                              size={36}
                              className={`transition-colors ${
                                star <= (hoveredRating || rating)
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-slate-300"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                      {rating > 0 && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-sm text-slate-600 mt-3"
                        >
                          {rating === 5 && "Excellent! 🎉"}
                          {rating === 4 && "Great! 👍"}
                          {rating === 3 && "Good"}
                          {rating === 2 && "Could be better"}
                          {rating === 1 && "Needs improvement"}
                        </motion.p>
                      )}
                    </div>

                    {/* Optional Text Feedback */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Any suggestions? (Optional)
                      </label>
                      <textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder="Tell us how we can improve..."
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-shadow"
                        rows={3}
                        maxLength={500}
                      />
                      <p className="text-xs text-slate-400 mt-1 text-right">
                        {feedbackText.length}/500
                      </p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="p-6 pt-0 flex gap-3">
                    <Button
                      variant="outline"
                      onClick={handleSkip}
                      className="flex-1"
                      disabled={isSubmitting}
                    >
                      Skip
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      className="flex-1"
                      disabled={rating === 0 || isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Submitting...
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <Send size={16} className="mr-2" />
                          Submit
                        </span>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
